import {useRef, useState} from 'react';
import {Button, ButtonGroup} from '@/components/Button';
import {SidePanelFilterRow, SidePanelFilterSection} from '@/components/SidePanel/StyledComponents';
import {KodataStoresQueryFilters} from '@/map/enums';
import {convertToMyfcCategoryFromKodata, toStringFromRange} from '@/map/utils';
import {FlexFullGrow, IconClose} from '@/styled-components';
import {FilterChip, FilterChips} from '../StyledComponents';
import {FilterModal} from './Modal';
import {SearchBar} from './SearchBar';

type FilterArrayType = Array<{key: KodataStoresQueryFilters; value: any}>;

interface IProps {
  disabled?: boolean;
  searchFilter?: FilterArrayType;
  area?: string;
  onSearch?(filters: FilterArrayType): void;
}

export const Filter = ({
  disabled,
  searchFilter: curFilter,
  area,
  onSearch,
}: IProps) => {
  const [openModal, setOpenModal] = useState<boolean>(false);

  const keywordRef = useRef<HTMLInputElement>(null);

  const hasSearchFilter = !!curFilter?.length;

  const searchKeyword =
    hasSearchFilter &&
    curFilter.find((i) => i.key === KodataStoresQueryFilters.nameRegex)?.value;

  const removePrevNameRegex = (filters: FilterArrayType) => {
    if (!filters) {
      return filters;
    }

    return filters.filter((i) => i.key !== KodataStoresQueryFilters.nameRegex);
  };

  const addInputNameRex = (filters: FilterArrayType) => {
    const filtered = removePrevNameRegex(filters) ?? [];
    const nameRegex = keywordRef.current?.value?.trim?.();

    return [
      ...filtered,
      {key: KodataStoresQueryFilters.nameRegex, value: nameRegex},
    ];
  };

  const onSearchText = (text: string) => {
    if (
      !onSearch ||
      !area?.length ||
      (!text?.length && !searchKeyword?.length)
    ) {
      return;
    }

    const prevFilter = (curFilter && removePrevNameRegex(curFilter)) ?? [];

    onSearch([
      ...prevFilter,
      {key: KodataStoresQueryFilters.nameRegex, value: text},
    ]);
  };

  const clearFilter = (key?: KodataStoresQueryFilters, value?: any) => {
    if (!onSearch || !curFilter) {
      return;
    }

    if (key) {
      const filtered = addInputNameRex(curFilter.filter((i) => i.key !== key));

      if (value) {
        const item = curFilter.find((i) => i.key === key);

        if (!item?.value) {
          return;
        }

        const nextValue = item.value.filter((i: any) => i !== value);

        if (!nextValue?.length) {
          onSearch(filtered);
        } else {
          onSearch([
            ...filtered,
            {
              key,
              value: nextValue,
            },
          ]);
        }
      } else {
        onSearch(filtered);
      }
    } else {
      onSearch([]);
    }
  };

  const handleSearch = (filters: FilterArrayType) => {
    onSearch?.(addInputNameRex(filters));
  };

  return (
    <SidePanelFilterSection>
      <SidePanelFilterRow>
        <ButtonGroup style={{position: 'relative'}}>
          <Button
            size="small"
            variant="outline"
            color="black"
            disabled={!hasSearchFilter || !curFilter.length}
            onClick={() => clearFilter()}
          >
            필터 초기화
          </Button>
          <FlexFullGrow />
          <Button
            size="small"
            variant="outline"
            color="black"
            disabled={disabled}
            onClick={() => setOpenModal(true)}
          >
            필터 설정
          </Button>
        </ButtonGroup>
      </SidePanelFilterRow>
      {(!!area?.length || !!curFilter?.length) && (
        <SidePanelFilterRow>
          <FilterChips>
            {!!area?.length && <FilterChip>{area}</FilterChip>}
            {curFilter?.map(
              (i) =>
                i.key !== KodataStoresQueryFilters.nameRegex &&
                (i.key === KodataStoresQueryFilters.salesRanges
                  ? (Array.isArray(i.value[0]) ? i.value : [i.value]).map(
                      (v: any, idx: number) => (
                        <FilterChip key={i.key + idx}>
                          {toStringFromRange(v, '원')}
                          <IconClose onClick={() => clearFilter(i.key, v)} />
                        </FilterChip>
                      )
                    )
                  : Array.isArray(i.value) &&
                    i.value.map((x, idx) => (
                      <FilterChip key={i.key + idx}>
                        {convertToMyfcCategoryFromKodata(x)}
                        <IconClose onClick={() => clearFilter(i.key, x)} />
                      </FilterChip>
                    )))
            )}
          </FilterChips>
        </SidePanelFilterRow>
      )}
      <SidePanelFilterRow>
        <SearchBar
          defaultValue={searchKeyword}
          disabled={disabled}
          ref={keywordRef}
          onSubmit={onSearchText}
        />
      </SidePanelFilterRow>
      {openModal && (
        <FilterModal
          area={area}
          currentFilters={curFilter}
          onSearch={handleSearch}
          onClose={() => setOpenModal(false)}
        />
      )}
    </SidePanelFilterSection>
  );
};
